import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

import SubMenu from '../components/SubMenu';
import DateFormat from '../components/DateFormat';
import SearchBarCP from '../components/SearchBarCP';
import ModalWindow from '../components/ModalWindow';
import { useUserData } from '../context/UserData';
import FormHeader from '../components/FormHeader';
import FormVarH from '../components/FormVarH';
import SubmitButton from '../components/SubmitButton';
import LoadingSpinner from '../components/LoadingSpinner';
import FormMoney from '../components/FormMoney';
import CurrencyFormat from '../components/CurrencyFormat';
import Modal from '../components/Modal';
import FileLocation from '../components/FileLocation';
import HeadingCp from '../components/HeadingCp';
import SelectCustomers from '../components/SelectCustomers';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const badge = (status) => {
  const color =
    status === 'Approved' ? 'bg-emerald-100 text-emerald-700 ring-emerald-200' :
    status === 'Pending'  ? 'bg-amber-100 text-amber-700 ring-amber-200' :
                             'bg-slate-100 text-slate-700 ring-slate-200';
  return `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${color}`;
};

const CotProposalPage = () => {
  const navigate = useNavigate();
  const userData = useUserData();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [file, setFile] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null); // will hold the proposal being edited
  const api = `${apiUrl}/cot_crud.php`;
  // keep new & edit modal selections independent
  const [selectedCompanyNew, setSelectedCompanyNew] = useState(null);
  const [selectedCompanyEdit, setSelectedCompanyEdit] = useState(null);

  const [priceOpenId, setPriceOpenId] = useState(null);
  const [editPrice, setEditPrice] = useState('');

  const [searchTerm, setSearchTerm] = useState('');

  const [form, setForm] = useState({
    customer_id: '',
    job_site: '',
    price: '',
    description: '',
    sentto: '',
    status: 'Pending',
  });

  // Debounced search (nice UX)
  const debounced = useMemo(() => {
    let t;
    return (val) => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (!val) { setFiltered(items); return; }
        const v = val.toLowerCase();
        const next = items.filter(o =>
          (o.description && o.description.toLowerCase().includes(v)) ||
          (o.job_site && o.job_site.toLowerCase().includes(v)) ||
          (o.customer_name && o.customer_name.toLowerCase().includes(v))
        );
        setFiltered(next);
      }, 250);
    };
  }, [items]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    debounced(v);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiUrl}/cot_crud.php?getCotProposals=true`);
        const data = Array.isArray(res.data) ? res.data : [];
        if (data.length === 0) toast.info('No proposals found', { position: 'top-center' });
        setItems(data);
        setFiltered(data);
      } catch (err) {
        // toast.error('Error loading proposals', { position: 'top-center' });
        if (err.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [apiUrl, navigate]);

  useEffect(() => {
    if (!editOpen || !editForm || !companies.length) return;
    const match = companies.find(c => Number(c.id) === Number(editForm.customer_id)) || null;
    if (match && (!selectedCompanyEdit || Number(selectedCompanyEdit.id) !== Number(match.id))) {
      setSelectedCompanyEdit(match);
    }
  }, [editOpen, editForm, companies]);

  // helper to DRY the status update
  const updateCotStatus = async (o, next) => {
    try {
      const r = await axios.post(`${apiUrl}/cot_crud.php`, {
        status: 'update_status_cot_proposal',
        data: { dt_id: o.id, dt_status: next }
      }, { headers: { 'Content-Type': 'application/json' }});
      if (r.data[0] === 'success') {
        setItems(s => s.map(x => x.id === o.id ? { ...x, status: next } : x));
        setFiltered(s => s.map(x => x.id === o.id ? { ...x, status: next } : x));
        toast.success('Status updated', { position: 'top-center' });
      } else {
        toast.error('Error updating status', { position: 'top-center' });
      }
    } catch {
      toast.error('Network error', { position: 'top-center' });
    }
  };

  const getLinksForCotProposal = (o) => {
    const links = [
      {
        name: o.status === 'Approved' ? 'Status Pending' : 'Status Approved',
        click: () => {
          const next = o.status === 'Approved' ? 'Pending' : 'Approved';
          updateCotStatus(o, next);
        },
        key: `status-main-${o.id}`,
      },
      {
        name: 'Edit Proposal',
        click: () => {
          // seed edit form + customer selection
          setEditForm(prev => ({
            ...(prev || {}),
            ...o,
            customer_id: o.customer_id,
            customer_name: o.customer_name,
          }));
          // keep your current selector shape (customer_id + customer_name)
          setSelectedCompanyEdit({ customer_id: o.customer_id, customer_name: o.customer_name });
          setEditOpen(true);
        },
        key: `edit-${o.id}`,
      },
    ];

    // Always offer Void toggle; label flips accordingly
    links.push({
      name: o.status === 'Void' ? 'Status Pending' : 'Status Void',
      click: () => {
        const next = o.status === 'Void' ? 'Pending' : 'Void';
        updateCotStatus(o, next);
      },
      key: `status-void-${o.id}`,
    });

    return links;
  };

  const handleCompanySelectEdit = (company) => {
  setSelectedCompanyEdit(company);
  if (company) {
    setEditForm(prev => ({
      ...prev,
      customer_id: company.id,
      customer_name: company.customer_name, // if you also show name in table/UI
    }));
  } else {
    // cleared
    setEditForm(prev => ({
      ...prev,
      customer_id: '',
      customer_name: '',
    }));
  }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const submitEdit = async (e) => {
    e.preventDefault();

    const cid = Number(editForm?.customer_id);
    if (!cid || Number.isNaN(cid) || cid <= 0) {
      toast.error('Please select a customer');
      return;
    }

    const payload = {
      status: 'update_cot_proposal_full',
      data: {
        id: Number(editForm.id),                  // proposal id
        customer_id: cid,                         // FK -> customers.id
        job_site: editForm.job_site,
        description: editForm.description ?? null,
        price: parseFloat(editForm.price || 0),
        sentto: editForm.sentto || null,
        status: editForm.status || 'Pending',
      }
    };
    const r = await axios.post(`${apiUrl}/cot_crud.php`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
    if (r.data[0] === 'success') {
      const updated = r.data[1];
      setItems(s => s.map(x => x.id === updated.id ? updated : x));
      setFiltered(s => s.map(x => x.id === updated.id ? updated : x));
      setEditOpen(false);
      toast.success('Proposal updated', { position: 'top-center' });
    } else {
      toast.error(r.data?.error || 'Update failed', { position: 'top-center' });
    }
  };

  const handleNewChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const submitNew = async (e) => {
    e.preventDefault();
    // setLoading(true);
    try {
      const payload = { status: 'submit_cot_proposal', data: form };
      const r = await axios.post(`${apiUrl}/cot_crud.php`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (r.data?.success) {
        setItems(s => [r.data.success, ...s]);
        setFiltered(s => [r.data.success, ...s]);
        setNewOpen(false);
        toast.success('Proposal created', { position: 'top-center' });
        setForm({ customer_id: '', job_site: '', price: '', description: '', sentto: '', status: 'Pending', date: '' });
      } else {
        toast.error(r.data?.error || 'Save failed', { position: 'top-center' });
      }
    } catch {
      toast.error('Network error', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = newCompany => {
    if (newCompany && newCompany.customer_name) {
      setSelectedCompany(newCompany);
      setForm(prev => ({
        ...prev,
        customer_name: newCompany.customer_name,
        customer_id: newCompany.id
      }));
    } else {
      //console.error('Invalid company selected:', newCompany);
    }
  };

  const handleSearchClick = async () => {
    setLoading(true);
    const bodyData = {
      status: 'searchCall',
      data: { search: searchTerm }
    };
    try {
      const response = await axios.post(api, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data && Array.isArray(response.data)) {
        setFiltered(response.data);
      } else {
        setFiltered([]);
        toast.error('Not found: ' + searchTerm, { position: 'top-center' });
      }
    } catch (error) {     
      if (error.response && error.response.status === 401) {
      navigate('/login');
      } else if (error.response && error.response.status === 403) {
      toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
      position: "top-center"
      });
      }
    } finally {
      setLoading(false);
    }
  };  

  return (
    <div className="mx-auto max-w-6xl sm:px-6 lg:px-8 py-10">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <HeadingCp label={'COT Proposals'} />
          {/* <h1 className="text-xl font-semibold leading-7 text-gray-900 py-4">COT Proposals</h1> */}
          <p className="text-sm text-gray-500">Create, search, and manage proposals.</p>
        </div>
      </div>

      <div className="mt-6 flex gap-3 items-center">
        <button
          type="button"
          onClick={() => navigate('/cot_billing')}
          className="rounded-lg bg-green-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
        >
          Cot Dashboard
        </button>
        <button
          type="button"
          onClick={() => setNewOpen(true)}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          New Proposal
        </button>
        <div className="flex-grow">
          {/* <SearchBarCP search={search} handleChange={onSearchChange} onClick={() => { /* server search optional */ }
          <SearchBarCP
          search={searchTerm}
          handleChange={(e) => setSearchTerm(e.target.value)}
          onClick={handleSearchClick}
        />
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm relative overflow-visible">
        <div className="shadow ring-1 ring-black ring-opacity-5 relative overflow-visible">
          <table className="min-w-full border-separate border-spacing-0">
            <thead className="top-0 z-10 bg-white">
              <tr>
                <th className="border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8">
                  ID 
                </th>
                <th className="border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8">
                  Customer /Job-Site
                </th>
                <th className="hidden border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:table-cell">
                  Price
                </th>
                <th className="hidden border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:table-cell">
                  Description
                </th>
                <th className="border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-3 pr-4 text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pr-6 lg:pr-8">
                  Employee Name
                </th>
                <th className="border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter">
                  Status
                </th>                
                <th className="border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-3 pr-4 text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pr-6 lg:pr-8">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((object, objectIdx) => (
              <tr
                key={object.id}
                className={`divide-x ${
                  object.status === 'Void'
                    ? 'bg-red-100'
                    : objectIdx % 2 === 0
                      ? 'bg-white'
                      : 'bg-gray-100'
                }`}>
                  {/* Actions */}
                  <td className={classNames(
                      objectIdx !== items.length - 1 ? 'border-b border-gray-200' : '',
                      'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8 '
                    )} >
                    <SubMenu label={object.id} idItem={object.id} links={getLinksForCotProposal(object)} />
                  </td>

                  {/* Service Number + Job Site */}
                  <td
                    className={classNames(
                      objectIdx !== items.length - 1 ? 'border-b border-gray-200' : '',
                      'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 lg:pl-8'
                    )} >
                    <span className="text-gray-900">{object.customer_name}</span>
                    <br />
                    <span className="text-gray-500">{object.job_site}</span>
                  </td>          

                  {/* Price */}
                  <td className="px-4">
                    <CurrencyFormat value={object.price} />
                  </td>

                  {/* Service Notes */}
                  <td
                    className={classNames(
                      objectIdx !== items.length - 1 ? 'border-b border-gray-200' : '',
                      'hidden px-3 py-4 text-sm text-gray-500 lg:table-cell'
                    )}
                  >
                    {object.description ? object.description : <span className="text-gray-400 italic">-</span>}

                  </td>

                  {/* Employee */}
                  <td className={classNames(
                      objectIdx !== items.length - 1 ? 'border-b border-gray-200' : '',
                      'table-cell align-middle text-center'
                    )}
                  >
                    {object.employee_name}
                  </td>
                  
                  {/* Status */}
                  <td className={classNames(
                      objectIdx !== items.length - 1 ? 'border-b border-gray-200' : '',
                      'whitespace-nowrap px-3 py-4 text-sm text-gray-500'
                    )} >
                    <span className={`font-bold ${object.status === 'Approved' ? 'bg-green-300 text-black' : 'text-gray-900'} p-1 rounded-lg`}>
                      {object.status}
                    </span>
                  </td>

                  {/* Date */}
                  <td
                    className={classNames(
                      objectIdx !== items.length - 1 ? 'border-b border-gray-200' : '',
                      'whitespace-nowrap px-3 py-4 text-sm text-gray-500'
                    )}
                  >
                    <DateFormat date={object.date} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Proposal */}
      {newOpen && (
        <ModalWindow
          job={
            <div className="py-2">
              <form onSubmit={submitNew} className="w-full">
                <FormHeader type={'New'} what={'COT Proposal'} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-3">
                    <SelectCustomers
                      label="Select a Company"
                      items={companies}
                      selectedItem={selectedCompany}
                      setSelectedItem={handleCompanySelect}
                      apiUrl={`${apiUrl}/customer_crud.php`}
                    />
                  </div>
                  {/* <FormVarH label={'Customer ID'} inside={'customer_id'} val={form.customer_id} onChange={handleNewChange} required /> */}
                  <FormVarH label={'Job Site'} inside={'job_site'} val={form.job_site} onChange={handleNewChange} required />
                  <FormMoney label={'Price'} inside={'price'} val={form.price} onChange={handleNewChange} required />
                  <FormVarH label={'Send To (email)'} inside={'sentto'} val={form.sentto} onChange={handleNewChange} />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-900">Description</label>
                  <textarea
                    rows={4}
                    name="description"
                    id="description"
                    value={form.description}
                    onChange={handleNewChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 text-gray-700 shadow-sm focus:ring-2 focus:ring-indigo-600"
                  />
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                  <button type="button" className="rounded-md px-3 py-2 ring-1 ring-gray-300" onClick={() => setNewOpen(false)}>Close</button>
                  <SubmitButton>Submit</SubmitButton>
                </div>
              </form>
            </div>
          }
          closeNotes={() => setNewOpen(false)}
        />
      )}

      {editOpen && (
        <ModalWindow
          job={
            <div className="py-2">
              <form onSubmit={submitEdit} className="w-full">
                <FormHeader type={'Edit'} what={'COT Proposal'} />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* <FormVarH
                    label={'Customer Name'}
                    inside={'customer_name'}
                    val={editForm.customer_name}
                    onChange={handleEditChange}
                    required
                  /> */}
                  <div className="sm:col-span-3">
                  <SelectCustomers
                    label="Select a Company"
                    items={companies}
                    selectedItem={selectedCompanyEdit}
                    setSelectedItem={handleCompanySelectEdit}
                    apiUrl={`${apiUrl}/customer_crud.php`}
                  />
                  </div>                  
                  <FormVarH
                    label={'Job Site'}
                    inside={'job_site'}
                    val={editForm.job_site}
                    onChange={handleEditChange}
                    required
                  />
                  <FormMoney
                    label={'Price'}
                    inside={'price'}
                    val={editForm.price}
                    onChange={handleEditChange}
                    required
                  />
                  <FormVarH
                    label={'Send To (email)'}
                    inside={'sentto'}
                    val={editForm.sentto}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-900">Description</label>
                  <textarea
                    rows={4}
                    name="description"
                    value={editForm.description}
                    onChange={handleEditChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 text-gray-700 shadow-sm focus:ring-2 focus:ring-indigo-600"
                  />
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    className="rounded-md px-3 py-2 ring-1 ring-gray-300"
                    onClick={() => setEditOpen(false)}
                  >
                    Close
                  </button>
                  <SubmitButton>Save</SubmitButton>
                </div>
              </form>
            </div>
          }
          closeNotes={() => setEditOpen(false)}
        />
      )}

    </div>
  );
};

export default CotProposalPage;
